import { Component, Input } from '@angular/core';
import {
  ICarePlan,
  ICDADetailed,
  IFhirCarePlan,
  IFhirCarePlanActivityDetail,
  IFhirDocumentReference,
} from 'src/app/interfaces/SHL/ICDADetailed';
import { SHLService } from 'src/app/services/shl.service';
import { SwalToastService } from 'src/app/services/swal.service';
import {
  IEHDSCodeableConcept,
  IEHDSHealthProfessional,
  IEHDSHumanName,
  IEHDSPeriod,
} from 'src/app/interfaces/DischargeReport/IEHDSCommon';
import { IEHDSDischargeReport } from 'src/app/interfaces/DischargeReport/IEHDSDischargeReport';
import {
  IEHDSCondition,
  IEHDSDeviceUse,
  IEHDSMedicationUse,
  IEHDSProcedure,
} from 'src/app/interfaces/DischargeReport/IEHDSClinical';
import moment from 'moment';
import { date_format } from 'src/date/date.configuration';

interface DetailRow {
  title: string;
  detail?: string;
}

interface DetailSection {
  title: string;
  text?: string;
  rows: DetailRow[];
}

@Component({
  selector: 'app-shl-patient-summary',
  templateUrl: './shl-patient-summary.component.html',
  styleUrls: ['./shl-patient-summary.component.css'],
  standalone: false,
})
export class ShlPatientSummaryComponent {
  @Input() data?: ICDADetailed;
  @Input() isLoading: boolean = false;
  @Input() location?: string;

  isVisible: boolean = false;

  constructor(
    private readonly shlService: SHLService,
    private readonly swalToastService: SwalToastService
  ) {}

  loadDetails() {
    this.isVisible = true;
  }

  handleCancel() {
    this.isVisible = false;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';

    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    return `${year}-${month}-${day}`;
  }

  valueOrFallback(value?: string | null): string {
    return value?.trim() || '-';
  }

  reportIdentifier(report: IEHDSDischargeReport): string {
    return this.valueOrFallback(this.reportIdentifierValue(report));
  }

  codeableConceptText(concept?: IEHDSCodeableConcept): string {
    return this.valueOrFallback(this.conceptValue(concept));
  }

  periodText(period?: IEHDSPeriod): string {
    return this.valueOrFallback(this.periodValue(period));
  }

  dischargeDiagnoses(report: IEHDSDischargeReport): string {
    const diagnoses = report.body?.courseOfEncounter?.diagnosticSummary ?? [];
    const values = diagnoses
      .map((diagnosis) => this.codeableConceptText(diagnosis.problem))
      .filter((value) => value !== '-');
    return this.valueOrFallback(values.join('; '));
  }

  dischargeDetails(report: IEHDSDischargeReport): string {
    const details = report.body?.dischargeDetails;
    return this.valueOrFallback(
      [details?.generatedNarrative, details?.note].filter(Boolean).join(' ')
    );
  }

  formatReportDate(value?: string): string {
    if (!value) return '-';
    if (/^\d{8}/.test(value)) return this.formatDate(value);

    const parsed = moment(value, moment.ISO_8601, true);
    return parsed.isValid() ? parsed.format(date_format) : value;
  }

  carePlanResource(plan: ICarePlan, index: number): IFhirCarePlan | undefined {
    return (
      plan.fhirResource ??
      plan.FhirResource ??
      this.data?.carePlanFhirResources?.[index]
    );
  }

  carePlanTitle(plan: ICarePlan, index: number): string {
    const resource = this.carePlanResource(plan, index);
    return this.valueOrFallback(
      plan.title ?? resource?.title ?? plan.description ?? resource?.description
    );
  }

  carePlanSubtitle(plan: ICarePlan, index: number): string {
    const resource = this.carePlanResource(plan, index);
    return this.valueOrFallback(
      this.joinDetails([
        plan.status ?? resource?.status,
        plan.intent ?? resource?.intent,
      ])
    );
  }

  carePlanSections(plan: ICarePlan, index: number): DetailSection[] {
    const resource = this.carePlanResource(plan, index);
    const sections: DetailSection[] = [];
    const summary = this.nonEmptyRows([
      { title: 'Description', detail: plan.description ?? resource?.description },
      { title: 'Status', detail: plan.status ?? resource?.status },
      { title: 'Intent', detail: plan.intent ?? resource?.intent },
      { title: 'Category', detail: plan.category },
      {
        title: 'Period',
        detail:
          this.periodValue({
            start: plan.periodStart ?? resource?.period?.start,
            end: plan.periodEnd ?? resource?.period?.end,
          }) ?? undefined,
      },
      { title: 'Notes', detail: plan.notes ?? resource?.note?.[0]?.text },
      {
        title: 'Created',
        detail: this.optionalReportDate(plan.created ?? resource?.created),
      },
    ]);
    if (summary.length) {
      sections.push({ title: 'Care Plan Details', rows: summary });
    }

    const groups = new Map<string, IFhirCarePlanActivityDetail[]>();
    for (const activity of resource?.activity ?? []) {
      if (!activity.detail) continue;
      const group = this.carePlanActivityGroup(activity.detail);
      groups.set(group, [...(groups.get(group) ?? []), activity.detail]);
    }
    for (const [title, activities] of groups) {
      sections.push({
        title,
        rows: activities.map((activity, activityIndex) => ({
          title:
            activity.description ??
            activity.code?.text ??
            `${title.replace(/s$/, '')} ${activityIndex + 1}`,
          detail: this.joinDetails([
            this.activityExtension(activity, 'care-plan-activity-state')
              ?.valueCode ?? activity.status,
            this.activityExtension(activity, 'care-plan-medication-action')
              ?.valueCode,
            this.periodValue(activity.scheduledPeriod),
          ]),
        })),
      });
    }

    const attachments = this.carePlanAttachments(resource);
    if (attachments.length) {
      sections.push({
        title: 'Attachments',
        rows: attachments.map((document, attachmentIndex) => {
          const attachment = document.content?.[0]?.attachment;
          return {
            title:
              attachment?.title ??
              document.description ??
              `Attachment ${attachmentIndex + 1}`,
            detail: this.joinDetails([
              document.description !== attachment?.title
                ? document.description
                : undefined,
              document.status,
              attachment?.contentType,
              this.formatSize(attachment?.size),
              this.optionalReportDate(attachment?.creation ?? document.date),
            ]),
          };
        }),
      });
    }
    return sections;
  }

  dischargeReportSections(report: IEHDSDischargeReport): DetailSection[] {
    const header = report.header;
    const body = report.body;
    const encounter = body?.encounterInformation?.encounter;
    const sections: Array<DetailSection | undefined> = [
      this.section('Report Details', [
        {
          title: 'Report Identifier',
          detail: this.reportIdentifierValue(report),
        },
        { title: 'Document Type', detail: this.conceptValue(header?.documentType) },
        { title: 'Status', detail: this.conceptValue(header?.status) },
        { title: 'Issued Date', detail: this.optionalReportDate(header?.date) },
        {
          title: 'Episode Period',
          detail: this.periodValue(encounter?.period ?? header?.period),
        },
        { title: 'Language', detail: this.conceptValue(header?.language) },
        { title: 'Version', detail: header?.version },
        {
          title: 'Event Type',
          detail: this.conceptList(header?.eventType),
        },
        {
          title: 'Service Specialty',
          detail: this.conceptList(header?.serviceSpecialty),
        },
        { title: 'Custodian', detail: header?.custodian?.name },
      ]),
      this.section(
        'Alerts',
        (body?.alerts?.medicalAlert ?? []).map((alert, index) => ({
          title: this.conceptValue(alert.description) ?? `Alert ${index + 1}`,
          detail: this.conceptValue(alert.header?.status),
        })),
        body?.alerts?.generatedNarrative
      ),
      this.section(
        'Encounter Information',
        [
          { title: 'Type', detail: this.conceptValue(encounter?.type) },
          { title: 'Status', detail: this.conceptValue(encounter?.header?.status) },
          { title: 'Period', detail: this.periodValue(encounter?.period) },
          { title: 'Reason', detail: encounter?.reasonString?.join(', ') },
          {
            title: 'Admitted By',
            detail: this.healthProfessionalName(encounter?.admission?.admitter),
          },
        ],
        this.joinDetails([
          body?.encounterInformation?.generatedNarrative,
          body?.encounterInformation?.note,
          encounter?.note,
        ])
      ),
      this.section(
        'Admission Evaluation',
        [],
        this.joinDetails([
          body?.admissionEvaluation?.generatedNarrative,
          body?.admissionEvaluation?.note,
        ])
      ),
      this.section(
        'Past Problems',
        this.conditionRows(body?.patientHistory?.pastProblems),
        body?.patientHistory?.generatedNarrative
      ),
      this.section(
        'Past Procedures',
        this.procedureRows(body?.patientHistory?.procedures)
      ),
      this.section(
        'Devices and Implants',
        this.deviceRows(body?.patientHistory?.devicesAndImplants)
      ),
      this.section(
        'Diagnoses',
        this.conditionRows(body?.courseOfEncounter?.diagnosticSummary),
        this.joinDetails([
          body?.courseOfEncounter?.generatedNarrative,
          body?.courseOfEncounter?.note,
        ])
      ),
      this.section(
        'Procedures',
        this.procedureRows(body?.courseOfEncounter?.procedures)
      ),
      this.section(
        'Medical Devices',
        this.deviceRows(body?.courseOfEncounter?.medicalDevicesAndImplants)
      ),
      this.section(
        'Pharmacotherapy',
        this.medicationRows(body?.courseOfEncounter?.pharmacotherapy)
      ),
      this.section(
        'Discharge Details',
        [],
        this.joinDetails([
          body?.dischargeDetails?.generatedNarrative,
          body?.dischargeDetails?.note,
        ])
      ),
      this.section(
        'Medication on Discharge',
        this.medicationRows(body?.medicationSummary?.medicationUse),
        this.joinDetails([
          body?.medicationSummary?.generatedNarrative,
          body?.medicationSummary?.note,
        ])
      ),
      this.section(
        'Care Plan and Follow-up',
        (body?.carePlan?.carePlan ?? []).map((carePlan, index) => ({
          title: carePlan.title ?? `Care Plan ${index + 1}`,
          detail: this.joinDetails([
            carePlan.description,
            this.conceptList(carePlan.activityCodeableConcept),
            this.periodValue(carePlan.period),
            this.conceptValue(carePlan.header?.status),
          ]),
        })),
        this.joinDetails([
          body?.carePlan?.generatedNarrative,
          body?.carePlan?.otherRecommendations,
        ])
      ),
      this.section('Author and Provenance', this.provenanceRows(report)),
      this.section(
        'Attachments',
        (report.attachments ?? report.presentedForm ?? []).map(
          (attachment, index) => ({
            title: attachment.title ?? `Attachment ${index + 1}`,
            detail: this.joinDetails([
              this.conceptValue(attachment.contentType),
              this.conceptValue(attachment.language),
              this.formatSize(attachment.size),
            ]),
          })
        )
      ),
    ];
    return sections.filter(
      (section): section is DetailSection => section !== undefined
    );
  }

  private section(
    title: string,
    rows: DetailRow[] = [],
    text?: string
  ): DetailSection | undefined {
    const visibleRows = this.nonEmptyRows(rows);
    const visibleText = text?.trim() || undefined;
    return visibleRows.length || visibleText
      ? { title, rows: visibleRows, text: visibleText }
      : undefined;
  }

  private nonEmptyRows(rows: DetailRow[]): DetailRow[] {
    return rows.filter((row) => row.detail?.trim());
  }

  private conceptValue(concept?: IEHDSCodeableConcept): string | undefined {
    return (
      concept?.text?.trim() ||
      concept?.coding?.find((coding) => coding.display?.trim())?.display?.trim() ||
      concept?.coding?.find((coding) => coding.code?.trim())?.code?.trim() ||
      undefined
    );
  }

  private conceptList(concepts?: IEHDSCodeableConcept[]): string | undefined {
    return this.joinDetails((concepts ?? []).map((value) => this.conceptValue(value)));
  }

  private periodValue(period?: IEHDSPeriod): string | undefined {
    return this.joinDetails(
      [
        this.optionalReportDate(period?.start),
        this.optionalReportDate(period?.end),
      ],
      ' – '
    );
  }

  private optionalReportDate(value?: string): string | undefined {
    return value ? this.formatReportDate(value) : undefined;
  }

  private joinDetails(
    values: Array<string | undefined | null>,
    separator = ' · '
  ): string | undefined {
    const visible = values
      .map((value) => value?.trim())
      .filter((value): value is string => !!value);
    return visible.length ? visible.join(separator) : undefined;
  }

  private humanName(name?: IEHDSHumanName): string | undefined {
    if (name?.text?.trim()) return name.text.trim();
    const parts = [
      ...(name?.prefix ?? []),
      ...(name?.given ?? []),
      ...(name?.family ?? []),
      ...(name?.suffix ?? []),
    ].filter((part) => part?.trim());
    return parts.length ? parts.join(' ') : undefined;
  }

  private healthProfessionalName(
    professional?: IEHDSHealthProfessional
  ): string | undefined {
    return this.humanName(professional?.name);
  }

  private conditionRows(values?: IEHDSCondition[]): DetailRow[] {
    return (values ?? []).map((condition, index) => ({
      title: this.conceptValue(condition.problem) ?? `Diagnosis ${index + 1}`,
      detail: this.joinDetails([
        this.conceptValue(condition.header?.status),
        this.conceptValue(condition.diagnosisAssertionStatus),
        this.conceptList(condition.category),
        condition.resolutionCircumstances,
      ]),
    }));
  }

  private procedureRows(values?: IEHDSProcedure[]): DetailRow[] {
    return (values ?? []).map((procedure, index) => ({
      title: this.conceptValue(procedure.code) ?? `Procedure ${index + 1}`,
      detail: this.joinDetails([
        this.conceptValue(procedure.header?.status),
        this.optionalReportDate(procedure.header?.date),
        procedure.note,
      ]),
    }));
  }

  private deviceRows(values?: IEHDSDeviceUse[]): DetailRow[] {
    return (values ?? []).map((deviceUse, index) => {
      const device = deviceUse.deviceEHDSDevice;
      return {
        title:
          this.conceptValue(deviceUse.deviceCodeableConcept) ??
          device?.name?.[0]?.value ??
          `Device ${index + 1}`,
        detail: this.joinDetails([
          this.conceptValue(deviceUse.header?.status),
          this.conceptList(device?.type),
          deviceUse.note,
          device?.note,
        ]),
      };
    });
  }

  private medicationRows(values?: IEHDSMedicationUse[]): DetailRow[] {
    return (values ?? []).map((medicationUse, index) => ({
      title:
        medicationUse.medication?.productName ??
        medicationUse.medication?.description ??
        this.conceptValue(
          medicationUse.medication?.identifyingCodeCodeableConcept?.[0]
        ) ??
        `Medication ${index + 1}`,
      detail: this.joinDetails([
        this.conceptValue(medicationUse.treatmentStatus) ??
          this.conceptValue(medicationUse.header?.status),
        this.conceptList(medicationUse.changeType),
        medicationUse.dosageInstructions?.renderedDosageInstruction,
        this.periodValue(medicationUse.periodOfUse),
        this.conceptList(medicationUse.reasonCodeableConcept),
        medicationUse.reasonString?.join(', '),
        medicationUse.note,
      ]),
    }));
  }

  private provenanceRows(report: IEHDSDischargeReport): DetailRow[] {
    const header = report.header;
    const rows: DetailRow[] = [];
    const authors = [
      ...(header?.authorEHDSHealthProfessional ?? []).map((author) =>
        this.healthProfessionalName(author)
      ),
      ...(header?.authorEHDSOrganisation ?? []).map((author) => author.name),
    ];
    const authorText = this.joinDetails(authors, ', ');
    if (authorText) rows.push({ title: 'Author', detail: authorText });

    for (const attestation of header?.attestation ?? []) {
      rows.push({
        title: 'Attestation',
        detail: this.joinDetails([
          this.healthProfessionalName(
            attestation.attesterEHDSHealthProfessional
          ),
          this.optionalReportDate(attestation.datetime),
        ]),
      });
    }
    for (const authentication of header?.legalAuthentication ?? []) {
      rows.push({
        title: 'Legal Authentication',
        detail: this.joinDetails([
          this.healthProfessionalName(
            authentication.legalAuthenticatorEHDSHealthProfessional
          ),
          authentication.legalAuthenticatorEHDSOrganisation?.name,
          this.optionalReportDate(authentication.datetime),
        ]),
      });
    }
    return rows;
  }

  private activityExtension(
    detail: IFhirCarePlanActivityDetail,
    name: string
  ) {
    return detail.extension?.find((extension) =>
      extension.url?.endsWith(`/${name}`)
    );
  }

  private carePlanActivityGroup(detail: IFhirCarePlanActivityDetail): string {
    const type = this.activityExtension(detail, 'care-plan-activity-type')
      ?.valueCode;
    if (type === 'appointment' || detail.kind === 'Appointment') {
      return 'Appointments';
    }
    if (type === 'medication' || detail.kind === 'MedicationRequest') {
      return 'Medications';
    }
    return 'Service Requests';
  }

  private carePlanAttachments(
    resource?: IFhirCarePlan
  ): IFhirDocumentReference[] {
    const references = new Set(
      (resource?.supportingInfo ?? [])
        .map((info) => info.reference)
        .filter((reference): reference is string => !!reference)
    );
    return (resource?.contained ?? []).filter(
      (item) =>
        item.resourceType === 'DocumentReference' &&
        (!references.size || (!!item.id && references.has(`#${item.id}`)))
    );
  }

  private formatSize(value?: string | number): string | undefined {
    const bytes = Number(value);
    if (value === undefined || !Number.isFinite(bytes)) return undefined;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  private reportIdentifierValue(
    report: IEHDSDischargeReport
  ): string | undefined {
    const identifier = report.header?.identifier?.[0];
    return identifier?.value?.trim() || identifier?.system?.trim() || undefined;
  }

  downloadCDA() {
    if (this.location) {
      this.shlService.downloadSHL(this.location).subscribe({
        next: (xmlString: string) => {
          const blob = new Blob([xmlString], { type: 'application/xml' });
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = url;
          a.download = 'SmartHealthLink.xml';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          window.URL.revokeObjectURL(url);
        },
        error: (error) => {
          this.swalToastService.toastError(
            error.error?.message || 'Failed to download the CDA.'
          );
        },
      });
    }
  }
}
